import React, { createContext, useContext, useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getSocialNetworks } from '../../actions/socialNetworks';
import type {
  SocialNetworkItem,
  SocialNetworksState,
} from '../../reducers/socialNetworks';

interface SocialNetworksContextValue {
  networks: SocialNetworkItem[];
  loading: boolean;
  loaded: boolean;
  error: unknown;
}

const SocialNetworksContext = createContext<SocialNetworksContextValue>({
  networks: [],
  loading: false,
  loaded: false,
  error: null,
});

export const SocialNetworksProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const social: SocialNetworksState = useSelector(
    (s: any) => s.socialNetworks,
    shallowEqual,
  ) ?? { loaded: false, loading: false, error: null, data: { items: [] } };
  const requestedRef = useRef(false);

  useEffect(() => {
    // Evita chamadas duplicadas quando vários consumidores montam no mesmo tick
    if (
      !requestedRef.current &&
      !social.loaded &&
      !social.loading &&
      !social.error
    ) {
      requestedRef.current = true;
      dispatch(getSocialNetworks() as any);
    }
  }, [social.loaded, social.loading, social.error, dispatch]);

  const value: SocialNetworksContextValue = {
    networks: social?.data?.items ?? [],
    loading: social.loading,
    loaded: social.loaded,
    error: social.error,
  };

  return (
    <SocialNetworksContext.Provider value={value}>
      {children}
    </SocialNetworksContext.Provider>
  );
};

export const useSocialNetworksContext = () => useContext(SocialNetworksContext);

export default SocialNetworksProvider;
